// https://react-select.com/styles

import { ModelEntitySearchResponseItem } from '@upright/common/model/search/types';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { StylesConfig } from 'react-select';

// OA: Note: Custom components may override these with varying results using !important
export const searchStyles: StylesConfig<ModelEntitySearchResponseItem> = {
  container: base => ({
    ...base,
    letterSpacing: 0,
    height: '100%',
  }),
  control: base => ({
    ...base,
    background: '#ffffff',
    boxShadow: 'none',
    display: 'flex',
    border: 'none',
    height: '100%',
  }),
  placeholder: base => ({
    ...base,
    color: BouColor.PLATFORM_TEXT_GRAY,
    fontWeight: 'normal',
    margin: 0,
    paddingLeft: 2,
    paddingBottom: 4,
    paddingTop: 0,
  }),
  clearIndicator: base => ({
    ...base,
    cursor: 'pointer',
  }),
  singleValue: base => ({
    ...base,
    paddingRight: '8px',
  }),
  menu: base => ({
    ...base,
  }),
  option: base => ({
    ...base,
  }),
  input: base => ({
    ...base,
    margin: 0,
    marginLeft: 2,
    paddingBottom: 3,
    paddingTop: 0,
  }),
};

/**
 * Makes the search look like a standard platform text input (per DESIGN.md):
 * gray-300 border, md radius, a focus ring, design-token typography. Pass via
 * the `styleOverride` prop. Shared by callers that embed the entity search as a
 * plain field rather than the bespoke hero search (e.g. the favorites add-box
 * and the leena-2 company runner).
 */
export const inputLikeSearchStyles: StylesConfig<ModelEntitySearchResponseItem> = {
  container: base => ({
    ...base,
    inlineSize: '100%',
  }),
  control: (base, state) => ({
    ...base,
    minHeight: '2rem',
    height: '2rem',
    borderRadius: 'var(--border-radius__md)',
    border: '1px solid',
    borderColor: state.isFocused ? 'var(--color__gray-400)' : 'var(--color__gray-300)',
    boxShadow: state.isFocused ? '0 0 0 3px var(--color__gray-300)' : 'none',
    backgroundColor: 'var(--color__white)',
    fontSize: 'var(--font-size__md)',
    paddingInlineStart: 'var(--space__sm)',
    paddingInlineEnd: 'var(--space__xs)',
    '&:hover': {
      borderColor: 'var(--color__gray-400)',
    },
  }),
  valueContainer: base => ({
    ...base,
    padding: 0,
  }),
  placeholder: base => ({
    ...base,
    color: 'var(--color__gray-500)',
    margin: 0,
    fontSize: 'var(--font-size__md)',
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
    color: 'var(--color__gray-800)',
  }),
  singleValue: base => ({
    ...base,
    color: 'var(--color__gray-800)',
    margin: 0,
  }),
  menu: base => ({
    ...base,
    // Render the (non-portaled) menu above following page content with a solid
    // background, so list rows aren't grayed/see-through.
    zIndex: 20,
    backgroundColor: 'var(--color__white)',
    marginTop: 'var(--space__xs)',
    border: '1px solid var(--color__gray-300)',
    borderRadius: 'var(--border-radius__md)',
    boxShadow: '0 4px 12px hsla(212, 9%, 38%, 0.15)',
  }),
};

export function getPlatformSearchStyle(): StylesConfig<ModelEntitySearchResponseItem> {
  return {
    control: (_base, state) => ({
      paddingLeft: 8,
      paddingRight: 8,
      height: 40,
      display: 'flex',
      alinItems: 'center',
      border: '1px solid',
      borderColor: state.isFocused ? `#444D56` : `#D0D7DE`,
      borderRadius: 4,
    }),
    valueContainer: base => ({
      ...base,
      paddingLeft: 4,
    }),
    placeholder: _base => ({
      marginTop: 1,
      fontSize: 14,
      color: '#6A737D',
      marginLeft: -4,
    }),
    container: base => ({
      ...base,
      fontSize: 14,
    }),
    menu: base => ({
      ...base,
      marginLeft: 1,
      width: 'calc(100% - 2px)',
    }),
  };
}
